import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { User, Login, ChangePassword, School, UserLastLogout, FaceLogs } from 'src/app/interfaces/user';
import { PasswordHistoryCheckRequest } from 'src/app/interfaces/password-policy';
import { Observable, Subject } from 'rxjs';
import { AnyMxRecord } from 'dns';
import { StorageService } from '../storage/storage.service';

@Injectable({
  providedIn: 'root',
})

export class UserService {

  constructor(private httpClient: HttpClient, private storageService: StorageService) {}

  getCountries() {
    return this.httpClient.get<any>('assets/data/countries.json')
        .toPromise()
}

  loginHeader(login: Login, recaptchaToken?: string) {
    var encrpytion = btoa(login.email + ':' + login.password);
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Basic ${encrpytion}`,
    });

    if (recaptchaToken) {
      headers = headers.set('X-Recaptcha-Token', recaptchaToken);
    }

    return {
      headers,
    };
  }

  logoutHeader(token?: any) {
    let headers = new HttpHeaders({
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `${token}`,
    });

    return {
      headers,
    };
  }

  login(login: Login) {
    return this.httpClient
      .post(environment.userServiceURL + '/login', {}, this.loginHeader(login))
      .toPromise();
  }

  loginNewV2(login: Login, recaptchaToken?: string) {
    const payLoad = {recaptchaToken: recaptchaToken || ''};
    return this.httpClient
      .post(environment.userServiceURL + '/login/new-v2', payLoad, this.loginHeader(login, recaptchaToken))
      .toPromise();
  } 

  loginAuth(login: Login, tokenCode: any) {
    return this.httpClient
      .post(environment.userServiceURL + '/login/auth?tokenCode=' + tokenCode, {}, this.loginHeader(login))
      .toPromise();
  }  

  loginSendAuth(login: Login) {
    return this.httpClient
      .post(environment.userServiceURL + '/login/send-login-auth', {}, this.loginHeader(login))
      .toPromise();
  }  

  private async getToken(): Promise<string | null> {
    try {
      const token = await this.storageService.get('token');
      return token;
    } catch (error) {
      return null;
    }
  }

  async logoutService() {
    return this.httpClient
      .post(environment.userServiceURL +'/login/logout', {}, this.logoutHeader(await this.getToken()))
      .toPromise();
  } 

  getProfile() {
    return this.httpClient
      .get(environment.userServiceURL + '/safe-road-users/profile')
      .toPromise();
  }

  getProfileWithOutToDoList(param: any) {
    return this.httpClient
      .get(environment.userServiceURL + '/safe-road-users/profile?withOutToDoList='+param)
      .toPromise();
  }

  editProfile(user: User) {
    return this.httpClient
      .put(environment.userServiceURL + '/safe-road-users/edit-profile', user)
      .toPromise();
  }

  register(user: User) {
    return this.httpClient
      .post(environment.userServiceURL + '/safe-road-users/register', user)
      .toPromise();
  }

  registerWithPhoto(user: User, studentUploadPhoto: any) {
    const formData = new FormData();
    formData.append(
      'file',
      studentUploadPhoto.blob,
      studentUploadPhoto.blob.name
    );
    formData.append('object', JSON.stringify(user));

    return this.httpClient
      .post(
        environment.userServiceURL + '/safe-road-users/register-with-photo',
        formData
      )
      .toPromise();
  }

  addStudentUserWithPhoto(user: User, studentUploadPhoto: any) {
    const formData = new FormData();
    formData.append(
      'file',
      studentUploadPhoto.blob,
      studentUploadPhoto.blob.name
    );
    formData.append('object', JSON.stringify(user));

    return this.httpClient
      .post(
        environment.userServiceURL +
          '/driving-school/add-student-user-with-photo',
        formData
      )
      .toPromise();
  }

  verifyAccount(code: any) {
    return this.httpClient
      .get(
        environment.userServiceURL +
          '/safe-road-users/email-confirmation?code=' +
          code
      )
      .toPromise();
  }

  resendCode(email: any) {
    return this.httpClient
      .get(
        environment.userServiceURL +
          '/sysAdmin/email/resend-confirmation?email=' +
          email
      )
      .toPromise();
  }

  getFaceSettings() {
    return this.httpClient
      .get(
        environment.userServiceURL +
          '/safe-road-users/face-settings?id=1'
      )
      .toPromise();
  }

  
  getFaceLoginSettings() {
    return this.httpClient
      .get(
        environment.userServiceURL +
          '/safe-road-users/getFaceLoginSettings'
      )
      .toPromise();
  }

  changePassword(password: ChangePassword) {
    return this.httpClient
      .post(
        environment.userServiceURL + '/sysAdmin/email/change-password',
        password
      )
      .toPromise();
  }

  getPasswordPolicy() {
    return this.httpClient
      .get(environment.userServiceURL + '/sysAdmin/password-policy')
      .toPromise();
  }

  checkPasswordHistory(payload: PasswordHistoryCheckRequest) {
    return this.httpClient
      .post(
        environment.userServiceURL + '/sysAdmin/password-policy/check-history',
        payload
      )
      .toPromise();
  }

  updateDrivingSchoolReferralCode(referralCode: any) {
    return this.httpClient
      .put(
        environment.userServiceURL +
          '/safe-road-users/update-ds-profile-id/' +
          referralCode,
        {}
      )
      .toPromise();
  }

  getUserLastLogout(email:any) {
    return this.httpClient
      .get(
        environment.userServiceURL +
          '/safe-road-users/get-user-last-logout?email='+email,
        {}
      )
      .toPromise();
  }

  getUserLastLogoutNoAuth(email:any) {
    return this.httpClient
      .get(
        environment.userServiceURL +
          '/safe-road-users/get-user-last-logout-no-auth?email='+email,
        {}
      )
      .toPromise();
  }

  updateUserLastLogout(user:UserLastLogout) {
    return this.httpClient
      .put(
        environment.userServiceURL +
          '/safe-road-users/update-user-last-logout',user,
        {}
      )
      .toPromise();
  }

  updateUserFaceFailedCount(user:UserLastLogout) {
    return this.httpClient
      .put(
        environment.userServiceURL +
          '/safe-road-users/update-user-face-failed-count',user,
        {}
      )
      .toPromise();
  }

  //Upload
  uploadProfilePhoto(file: any) {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.post(
      environment.userServiceURL + '/upload-photo',
      formData,
      {
        reportProgress: true,
        observe: 'events',
      }
    );
  }

  getDsProfileById(id: any) {
    return this.httpClient
      .get(environment.userServiceURL + '/driving-school/view-profile/?' + id)
      .toPromise();
  }

  getDsProfileByDsCode(dsCode: any) {
    return this.httpClient
      .get(environment.userServiceURL + '/driving-school/view-profile-dsCode?dsCode=' + dsCode)
      .toPromise();
  }

  getDsListByBranchCode(branchCode: any){
    return this.httpClient
    .get(environment.userServiceURL + '/driving-school/driving-school-list-branch-code?branchCode=' + branchCode)
    .toPromise();
  }

  //Driving School

  getAllDrivingSchool() {
    return this.httpClient
      .get(environment.userServiceURL + '/driving-school/driving-school-list')
      .toPromise();
  }

  getAllValidatedDrivingSchool() {
    return this.httpClient
      .get(environment.userServiceURL + '/driving-school/driving-school-list-validated')
      .toPromise();
  }

  getShoolProfile() {
    return this.httpClient
      .get(environment.userServiceURL + '/driving-school/view-profile')
      .toPromise();
  }

  editSchoolProfile(schoolProfile: School) {
    return this.httpClient
      .put(
        environment.userServiceURL + '/driving-school/edit-profile',
        schoolProfile
      )
      .toPromise();
  }

  getStudentList() {
    return this.httpClient
      .get(environment.userServiceURL + '/driving-school/student-list')
      .toPromise();
  }

  getUserList() {
    return this.httpClient
      .get(environment.userServiceURL + '/driving-school/user-list')
      .toPromise();
  }

  getStudentProfile(studentId: any) {
    return this.httpClient
      .get(
        environment.userServiceURL +
          '/driving-school/view-student-user/' +
          studentId
      )
      .toPromise();
  }

  getStudentProfileV2(studentId: any) {
    return this.httpClient
      .get(
        environment.userServiceURL +
          '/driving-school/view-student-profile/' +
          studentId
      )
      .toPromise();
  }

  editStudentProfile(studentId: any, user: User) {
    return this.httpClient
      .put(
        environment.userServiceURL +
          '/driving-school/edit-student-user/' +
          studentId,
        user
      )
      .toPromise();
  }

  addStudent(user: User) {
    return this.httpClient
      .post(
        environment.userServiceURL + '/driving-school/add-student-user',
        user
      )
      .toPromise();
  }

  deleteStudentProfile(studentId: any) {
    return this.httpClient
      .delete(
        environment.userServiceURL +
          '/driving-school/delete-student-user/' +
          studentId
      )
      .toPromise();
  }

  updateUserLuxand(luxandId: any) {
    var obj = {
      luxandId: luxandId,
    };
    return this.httpClient
      .put(
        environment.userServiceURL + '/safe-road-users/update-luxand-id',
        luxandId
      )
      .toPromise();
  }

  // Luxand
  loadHeader() {
    return {
      headers: new HttpHeaders({
        token:  '6265519eb3ee4daf850419432ed35954', //old account (raffymagracia@gmail.com)
        //token:  'df16be834f434cd787f8064b2d41a930', //new account (admin@saferoads.ph)
        Accept: 'multipart/form-data',
      }),
    };
  }

  // Luxand
  loadHeader2() {
    return {
      headers: new HttpHeaders({
        Accept: 'application/json',
      }),
    };
  }

  registerLuxand(obj: any, email: any) {
     var file = new File([obj.blob], obj.filepath, {
      lastModified: Date.now(),
    }); 

    var formData = new FormData();
    formData.append('name', email);
    formData.append('photos', file, 'file');
    formData.append('store', '1');
    return this.httpClient
      .post<any>(
        environment.luxandURL + '/v2/person',
        formData,
        this.loadHeader()
      )
      .toPromise();
  }

  registerLuxandv2(obj: any, email: any) {

   var formData = new FormData();
   formData.append('name', email);
   formData.append('photos', obj);
   formData.append('store', '1');
   return this.httpClient
     .post<any>(
       environment.luxandURL + '/v2/person',
       formData,
       this.loadHeader()
     )
     .toPromise();
 }

  forgotPassword(obj: any) {
    return this.httpClient
      .post(environment.userServiceURL + '/safe-road-users/forgotPassword', obj)
      .toPromise();
  }

  updateLuxand(obj: any, luxandId: any) {
    var file = new File([obj.blob], obj.filepath, {
      lastModified: Date.now(),
    });

    var formData = new FormData();
    formData.append('photo', file, 'file');
    formData.append('store', '1');
    return this.httpClient
      .post<any>(
        environment.luxandURL + '/subject/' + luxandId,
        formData,
        this.loadHeader()
      )
      .toPromise();
  }

  deleteLuxand(luxandId: any) {
    return this.httpClient
      .delete<any>(
        environment.luxandURL + '/subject/' + luxandId,
        this.loadHeader()
      )
      .toPromise();
  }

  deleteLuxandPerson(uuid: any) {
    return this.httpClient
      .delete<any>(
        environment.luxandURL + '/person?uuid='+ uuid,
        this.loadHeader()
      )
      .toPromise();
  }

  getLuxandIdByEmail(email: any) {
    return this.httpClient
      .get<any>(
        environment.userServiceURL +
          '/safe-road-users/get-luxand-id?email=' +
          email,
        this.loadHeader2()
      )
      .toPromise();
  }

  getFaceKeyByEmail(email: any) {
    return this.httpClient
      .get<any>(
        environment.userServiceURL +
          '/safe-road-users/get-face-key?email=' +
          email,
        this.loadHeader2()
      )
      .toPromise();
  }

  verificationLuxand(obj: any, luxandId: any) {
    var file = new File([obj.blob], obj.filepath, {
      lastModified: Date.now(),
    });

    var formData = new FormData();
    formData.append('photo', file, 'file');
    return this.httpClient
      .post<any>(
        environment.luxandURL + '/photo/verify/' + luxandId,
        formData,
        this.loadHeader()
      )
      .toPromise();
  }

  similarityLuxand(obj1: any, obj2: any) {

/*      var file1 = new File([obj1.blob], obj1.filepath, {
      lastModified: Date.now(),
    }); 
     var file2 = new File([obj2.blob], obj2.filepath, {
      lastModified: Date.now(),
    }); 
    console.log(file1);
    console.log(file2); */
    var formData = new FormData();
    formData.append('face1', obj1);
    formData.append('face2', obj2);
    formData.append('threshold', '0.8');
    return this.httpClient
      .post<any>(
        environment.luxandURL + '/photo/similarity',
        formData,
        this.loadHeader()
      )
      .toPromise();
  }

  verificationLuxandV2(obj: any) {

    var formData = new FormData();
    formData.append('photo', obj);
    return this.httpClient
      .post<any>(
        environment.luxandURL + '/photo/search/v2',
        formData,
        this.loadHeader()
      )
      .toPromise();
  }

   // Luxand
   loadHeaderLiveness() {
    return {
      headers: new HttpHeaders({
        token:  '6265519eb3ee4daf850419432ed35954', //old account (raffymagracia@gmail.com)
        //token:  'df16be834f434cd787f8064b2d41a930', //new account (admin@saferoads.ph)        
        // 'Content-Type': 'multipart/form-data;',
        // Accept: 'multipart/form-data',
      }),
    };
  }

checkImageLiveness(obj: any) {

  var formData = new FormData();
  formData.append('photo', obj);
  return this.httpClient
    .post<any>(
      environment.luxandURL + '/photo/liveness/v2',
      formData,
      this.loadHeaderLiveness()
    )
    .toPromise();
}

  faceLogin(faceKey: any, luxandId: any) {
    var obj = {
      faceKey: faceKey,
      luxandId: luxandId,
    };
    return this.httpClient
      .post<any>(environment.userServiceURL + '/login/face-login', obj)
      .toPromise();
  }

  faceLoginV2(faceKey: any, email: any) {
    var obj = {
      faceKey: faceKey,
      email: email,
    };
    return this.httpClient
      .post<any>(environment.userServiceURL + '/login/face-login-v2', obj)
      .toPromise();
  }

  uploadFaceLogV2(faceLogs: FaceLogs, studentUploadPhoto: any) {
    const formData = new FormData();
    formData.append(
      'file',
      studentUploadPhoto,
      studentUploadPhoto.name
    );
    formData.append('object', JSON.stringify(faceLogs));

    return this.httpClient
      .post(
        environment.userServiceURL + '/safe-road-users/create-face-recognition-image',
        formData
      )
      .toPromise();
  }

  uploadFaceLog(faceLogs: FaceLogs, studentUploadPhoto: any) {
    const formData = new FormData();
    formData.append(
      'file',
      studentUploadPhoto.blob,
      studentUploadPhoto.blob.name
    );
    formData.append('object', JSON.stringify(faceLogs));

    return this.httpClient
      .post(
        environment.userServiceURL + '/safe-road-users/create-face-recognition-image',
        formData
      )
      .toPromise();
  }

  getFaceLogs(studentId: any) {
    return this.httpClient
      .get<any>(
        environment.userServiceURL +
          '/safe-road-users/get-student-face-recognition-images?studentId=' +
          studentId,
      )
      .toPromise();
  }

  getFaceTopics() {
    return this.httpClient
      .get<any>(
        environment.userServiceURL +
          '/safe-road-users/get-face-topics'
          ,
      )
      .toPromise();
  }

  getStudentFaceCapturedLogs(studentId: any) {
    return this.httpClient
        .get(
            environment.userServiceURL +
                "/safe-road-users/get-student-face-recognition-images?studentId=" +
                studentId
        )
        .toPromise();
}

  getStudentDetailsTempStorage(referenceNumber) {
    return this.httpClient
      .get(
        environment.userServiceURL +
          '/safe-road-user-payment-logs/by-payment-reference-No-no-auth?paymentReferenceNo=' +
          referenceNumber
      )
      .toPromise();
  }

  checkEmail(email) {
    return this.httpClient
      .get(
        environment.userServiceURL +
          '/validation-service/email-exist-no-auth?email=' +
          email
      )
      .toPromise();
  }

  getUserFaceSettings(studentId: any) {
    return this.httpClient
      .get(
        environment.userServiceURL +
          '/safe-road-users/get-user-face-setting?studentId=' + studentId
      )
      .toPromise();
  }
  
}
